import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup, setPersistence, browserLocalPersistence, browserSessionPersistence, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, googleProvider, db } from '../lib/firebase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Mail, Lock, Chrome, ArrowRight, Zap, ChevronLeft, Star, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

// Importação do novo componente de recuperação
import Forgot from './Forgot';

// Ativos
import logoImg from '../assets/logo-semfrase.png';
import planoFundo from "../assets/planodefundo.png"; 

const Login = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [keepConnected, setKeepConnected] = useState(true);
  const [showForgotModal, setShowForgotModal] = useState(false); // Estado para o Modal
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

 const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // 1. Faz a autenticação inicial no Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, formData.email, formData.password);
      const firebaseUser = userCredential.user;

      // 2. Busca o documento dele no Firestore para checar o status
      const userRef = doc(db, "users", firebaseUser.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists() && userDoc.data().status === 'banned') {
        toast.error('Esta conta foi desativada por violação dos termos de uso.');
        await signOut(auth); // Desloga imediatamente
        setLoading(false);
        return; // Barra o navigate
      }

      await setPersistence(auth, keepConnected ? browserLocalPersistence : browserSessionPersistence);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Erro ao fazer login. Verifique suas credenciais.');
    } finally {
      setLoading(false);
    }
  };

const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      await setPersistence(auth, keepConnected ? browserLocalPersistence : browserSessionPersistence);
      const result = await signInWithPopup(auth, googleProvider);
      const firebaseUser = result.user;

      const userRef = doc(db, "users", firebaseUser.uid);
      const userDoc = await getDoc(userRef);

      if (userDoc.exists()) {
        // TRAVA DE BANIMENTO VIA GOOGLE
        if (userDoc.data().status === 'banned') {
          toast.error('Esta conta foi desativada por violação dos termos de uso.');
          await signOut(auth);
          setLoading(false);
          return;
        }

        toast.success('Login com Google realizado com sucesso!');
        navigate('/dashboard');
      } else {
        // Se for usuário novo, mantém o fluxo que a gente já corrigiu...
        const importedData = {
          name: firebaseUser.displayName?.toUpperCase() || '',
          email: firebaseUser.email || '',
          photoURL: firebaseUser.photoURL ? firebaseUser.photoURL.replace('s96-c', 's400-c') : null
        };

        toast.info('Identificamos seu login, mas seu cadastro está incompleto. Conclua-o agora!');
        navigate('/register', { state: { googleData: importedData } });
        
        setTimeout(async () => {
          await signOut(auth);
        }, 500);
      }
    } catch (error) {
      console.error('Google login error:', error);
      if (error.code === 'auth/popup-blocked') {
        toast.error('Popup bloqueado! Permita popups para este site.');
      } else {
        toast.error('Erro ao fazer login com Google.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex font-sans text-slate-900 bg-white overflow-hidden">
      
      {/* COLUNA ESQUERDA: INFORMAÇÕES (Visual Premium) */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-slate-50 items-center justify-center p-12 overflow-hidden border-r border-slate-100">
        <div className="absolute inset-0 z-0 opacity-[0.03] grayscale" 
             style={{ backgroundImage: `url(${planoFundo})`, backgroundSize: 'cover' }}>
        </div>

        <div className="relative z-20 max-w-lg text-center lg:text-left text-left">
          <div className="bg-white p-8 rounded-[3rem] shadow-2xl mb-12 inline-block border border-slate-50">
            <img src={logoImg} alt="Socorre Aí" className="w-40" />
          </div>
          
          <h1 className="text-5xl font-black uppercase italic tracking-tighter leading-[0.9] mb-8 text-blue-900">
            SOLUÇÕES <br /> RÁPIDAS PARA <br /> <span className="text-orange-500 underline decoration-8">SEU DIA A DIA.</span>
          </h1>

          <div className="space-y-6">
            <div className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-200">
                <Star className="w-6 h-6 text-white fill-white" />
              </div>
              <p className="font-black italic uppercase text-sm tracking-widest text-slate-700">Qualidade garantida</p>
            </div>
            
            <div className="flex items-center gap-4 group">
              <div className="w-12 h-12 rounded-2xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-200">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <p className="font-black italic uppercase text-sm tracking-widest text-slate-700">Técnicos de confiança</p>
            </div>
          </div>
        </div>
      </div>

      {/* COLUNA DIREITA: FORMULÁRIO */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-white relative">
        
        <button onClick={() => navigate('/')} className="absolute top-8 left-8 flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-blue-600 transition-colors">
          <ChevronLeft className="w-4 h-4" /> Início
        </button>

        <div className="w-full max-w-md text-left">
          <div className="text-center lg:text-left mb-10">
            <img src={logoImg} alt="Logo" className="w-24 mx-auto lg:ml-0 mb-6 lg:hidden" />
            <h2 className="text-4xl font-black uppercase italic tracking-tighter text-blue-900 leading-none mb-2">ACESSAR CONTA</h2>
            <p className="text-sm font-bold text-slate-400 uppercase tracking-widest italic text-left">Informe seus dados para entrar</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-[10px] font-black uppercase ml-4 text-slate-400 tracking-[0.2em] italic">E-mail</Label>
              <div className="relative group">
                <Mail className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 group-focus-within:text-blue-600 transition-colors" />
                <Input
                  type="email"
                  placeholder="seu@email.com"
                  className="h-16 pl-14 rounded-2xl border-slate-100 bg-slate-50 font-bold focus-visible:ring-blue-600 transition-all text-base"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center px-4">
                <Label className="text-[10px] font-black uppercase text-slate-400 tracking-[0.2em] italic">Senha</Label>
                {/* BOTÃO QUE ABRE O MODAL FORGOT */}
                <button 
                  type="button" 
                  onClick={() => setShowForgotModal(true)} 
                  className="text-[10px] font-black uppercase text-blue-600 hover:underline italic"
                >
                  Esqueceu a sua senha?
                </button>
              </div>
              <div className="relative group">
                <Lock className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-300 group-focus-within:text-blue-600 transition-colors" />
                <Input
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="h-16 pl-14 pr-12 rounded-2xl border-slate-100 bg-slate-50 font-bold focus-visible:ring-blue-600 transition-all text-base"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                />
                <button 
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-3 px-2 py-2 text-left">
              <Checkbox 
                id="keep" 
                checked={keepConnected} 
                onCheckedChange={(checked) => setKeepConnected(checked)}
                className="border-slate-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600 rounded-md w-5 h-5"
              />
              <label htmlFor="keep" className="text-[10px] font-black uppercase text-slate-500 cursor-pointer select-none tracking-widest italic leading-none">
                Manter conectado neste dispositivo
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full h-20 bg-blue-600 hover:bg-blue-700 text-white font-black rounded-3xl shadow-2xl shadow-blue-200 uppercase italic tracking-tighter text-xl active:scale-95 transition-all"
              disabled={loading}
            >
              <div className="flex items-center justify-center gap-3">
                <span>{loading ? "PROCESSANDO..." : "ENTRAR NO SISTEMA"}</span>
                {!loading && <ArrowRight className="w-6 h-6" />}
              </div>
            </Button>
          </form>

          <div className="my-10 relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-100"></div></div>
            <div className="relative flex justify-center text-[9px] font-black uppercase tracking-[0.4em]"><span className="bg-white px-6 text-slate-300">ACESSO RÁPIDO</span></div>
          </div>

          <Button
            type="button"
            onClick={handleGoogleLogin}
            variant="outline"
            className="w-full h-16 border-2 border-slate-100 rounded-2xl flex items-center justify-center gap-3 font-black uppercase italic text-xs hover:bg-slate-50 transition-all shadow-sm"
            disabled={loading}
          >
            <div className="flex items-center gap-3">
              <Chrome className="w-5 h-5 text-blue-600" />
              <span>Entrar com Google</span>
            </div>
          </Button>

          <p className="mt-12 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">
            Novo por aqui?{" "}
            <Link to="/register" className="text-orange-500 hover:text-orange-600 underline underline-offset-8 decoration-2 italic">
              Crie sua conta grátis
            </Link>
          </p>
        </div>
      </div>

      {/* COMPONENTE FORGOT SOBREPOSTO */}
      <Forgot isOpen={showForgotModal} onOpenChange={setShowForgotModal} />
      
    </div>
  );
};

export default Login;